import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, Send, Eye, MousePointerClick, MessageSquare, Sparkles, RefreshCw, Zap, PlusCircle } from "lucide-react";

const Stat = ({ icon: Icon, label, value, rate, testid }) => (
  <div className="brutalist-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="section-label inline-flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      {rate != null && (
        <span className="text-xs font-mono font-bold px-2 py-0.5 bg-[var(--peak-ink)] text-white">{Math.round((rate || 0) * 100)}%</span>
      )}
    </div>
    <div className="font-heading font-black text-5xl tracking-tighter mt-2" data-testid={`${testid}-value`}>{value}</div>
  </div>
);

const Bar = ({ pct, color = "bg-[var(--peak-blue-deep)]" }) => (
  <div className="h-2 bg-[var(--peak-surface-alt)] relative overflow-hidden">
    <div className={`h-full ${color}`} style={{ width: `${Math.min(100, Math.round((pct || 0) * 100))}%` }} />
  </div>
);

export default function WorkflowReport() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null); // {insight, actions, generated_at}
  const [insightLoading, setInsightLoading] = useState(false);
  const [applyingAction, setApplyingAction] = useState(null); // action.type currently running

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/workflows/${id}/report`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const loadInsight = async () => {
    setInsightLoading(true);
    try {
      const { data } = await api.get(`/workflows/${id}/report/insights`);
      setInsight(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInsightLoading(false); }
  };
  const applyPromote = async (winner) => {
    if (!confirm(`Promote ${winner} and pause every other variant?`)) return;
    setApplyingAction("promote_variant");
    try {
      await api.post(`/workflows/${id}/actions/promote-variant`, { winner });
      toast.success(`${winner} promoted — others paused.`);
      await load();
      await loadInsight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplyingAction(null); }
  };
  const applySuggest = async () => {
    setApplyingAction("suggest_variant");
    try {
      const { data } = await api.post(`/workflows/${id}/actions/suggest-variant`);
      toast.success(`New variant added: ${data.new_variant?.name} — "${(data.new_variant?.subject || "").slice(0, 50)}"`);
      await load();
      await loadInsight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplyingAction(null); }
  };
  useEffect(() => { load(); }, [id]);

  if (loading || !report) {
    return <div className="p-8 text-sm text-[var(--peak-muted)]" data-testid="wf-report-loading">Loading report…</div>;
  }

  const t = report.totals || {};
  const variants = report.variants || [];
  const recent = report.recent_replies || [];

  return (
    <div className="p-8 space-y-6" data-testid="workflow-report-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/automations" className="section-label inline-flex items-center gap-1 hover:text-[var(--peak-accent)]" data-testid="back-to-automations">
            <ArrowLeft className="w-3 h-3" /> Automations
          </Link>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2" data-testid="wf-report-name">{report.workflow_name}</h1>
          <div className="section-label mt-1">
            Trigger · {report.trigger?.type || "?"}
            {report.enabled ? <span className="ml-3 text-[#008A27]">● LIVE</span> : <span className="ml-3 text-[var(--peak-muted)]">◌ DISABLED</span>}
          </div>
        </div>
        <div className="inline-flex items-center gap-2 bg-[var(--peak-ink)] text-white px-3 py-2 font-bold uppercase text-xs tracking-widest">
          <BarChart3 className="w-4 h-4" /> Campaign report
        </div>
      </div>

      {/* AI Insights */}
      <div className="brutalist-card p-6" data-testid="ai-insights-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="section-label inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[var(--peak-blue-deep)]" /> AI Insights · Growth coach
          </div>
          <button
            onClick={loadInsight}
            disabled={insightLoading}
            data-testid={insight ? "regenerate-insight-btn" : "generate-insight-btn"}
            className="bg-[var(--peak-blue-deep)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest px-3 py-2 inline-flex items-center gap-1 disabled:opacity-60"
          >
            {insight ? <RefreshCw className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
            {insightLoading ? "Analyzing…" : insight ? "Regenerate" : "Generate insight"}
          </button>
        </div>
        {!insight && !insightLoading && (
          <p className="text-sm text-[var(--peak-muted)] mt-3">Ask Claude to read the numbers below and tell you what&apos;s working, what&apos;s not, and what to try next.</p>
        )}
        {insightLoading && (
          <div className="mt-3 space-y-2">
            <div className="h-3 bg-[var(--peak-surface-alt)] animate-pulse w-11/12" />
            <div className="h-3 bg-[var(--peak-surface-alt)] animate-pulse w-9/12" />
            <div className="h-3 bg-[var(--peak-surface-alt)] animate-pulse w-10/12" />
          </div>
        )}
        {insight && !insightLoading && (
          <div className="mt-3">
            <p className="text-base leading-relaxed" data-testid="insight-text">{insight.insight}</p>
            {(insight.actions || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--peak-border)] flex flex-wrap gap-2" data-testid="insight-actions">
                {(insight.actions || []).map((a) => {
                  const busy = applyingAction === a.type;
                  const isPromote = a.type === "promote_variant";
                  const Icon = isPromote ? Zap : PlusCircle;
                  const onClick = () => isPromote ? applyPromote(a.payload?.winner) : applySuggest();
                  return (
                    <button
                      key={a.type}
                      onClick={onClick}
                      disabled={busy || applyingAction != null}
                      data-testid={`action-${a.type}`}
                      title={a.reason || ""}
                      className={`inline-flex items-center gap-1.5 font-bold uppercase text-xs tracking-widest px-3 py-2 disabled:opacity-60 ${isPromote ? "bg-[#008A27] hover:bg-black text-white" : "bg-[var(--peak-accent)] hover:bg-black text-white"}`}
                    >
                      <Icon className="w-3 h-3" /> {busy ? "Applying…" : a.label}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="section-label mt-3 text-[var(--peak-muted)]">generated {new Date(insight.generated_at).toLocaleTimeString()}</div>
          </div>
        )}
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Send} label="Sent" value={t.sent || 0} testid="stat-sent" />
        <Stat icon={Eye} label="Opens" value={t.opened || 0} rate={t.open_rate} testid="stat-opens" />
        <Stat icon={MousePointerClick} label="Clicks" value={t.clicked || 0} rate={t.click_rate} testid="stat-clicks" />
        <Stat icon={MessageSquare} label="Replies" value={t.replied || 0} rate={t.reply_rate} testid="stat-replies" />
      </div>

      {/* Variant breakdown */}
      {variants.length > 0 && (
        <div className="brutalist-card p-6" data-testid="variants-table">
          <div className="section-label mb-4">Per-variant breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[var(--peak-ink)]">
                  {["Variant", "Sent", "Delivered", "Opened", "Open rate", "Clicked", "Click rate"].map((h) => (
                    <th key={h} className="text-left section-label py-2 pr-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variants.map((v) => (
                  <tr key={v.variant} className="border-b border-[var(--peak-border)]" data-testid={`row-variant-${v.variant}`}>
                    <td className="py-3 pr-4 font-heading font-bold uppercase tracking-tight">{v.variant}</td>
                    <td className="py-3 pr-4 font-mono">{v.sent}</td>
                    <td className="py-3 pr-4 font-mono">{v.delivered}</td>
                    <td className="py-3 pr-4 font-mono">{v.opened}</td>
                    <td className="py-3 pr-4 w-40"><div className="flex items-center gap-2"><Bar pct={v.open_rate} /><span className="font-mono text-xs">{Math.round((v.open_rate || 0) * 100)}%</span></div></td>
                    <td className="py-3 pr-4 font-mono">{v.clicked}</td>
                    <td className="py-3 pr-4 w-40"><div className="flex items-center gap-2"><Bar pct={v.click_rate} color="bg-[var(--peak-accent)]" /><span className="font-mono text-xs">{Math.round((v.click_rate || 0) * 100)}%</span></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent replies */}
      <div className="brutalist-card p-6" data-testid="recent-replies">
        <div className="section-label mb-4 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Recent replies</div>
        {recent.length === 0 && (
          <div className="text-sm text-[var(--peak-muted)]">No replies yet. When a contact replies to any email sent by this workflow, they&apos;ll show up here.</div>
        )}
        {recent.length > 0 && (
          <ul className="divide-y divide-[var(--peak-border)]">
            {recent.map((r) => (
              <li key={r.activity_id} className="py-3 flex items-start justify-between gap-4" data-testid={`reply-${r.activity_id}`}>
                <div className="min-w-0">
                  <Link to={`/contacts/${r.contact_id}`} className="font-bold uppercase text-xs tracking-widest hover:text-[var(--peak-accent)] truncate block">{r.contact_name}</Link>
                  <div className="text-xs text-[var(--peak-muted)] mt-0.5 truncate">{r.contact_email}</div>
                  <div className="text-sm mt-1 truncate">{r.subject || "(no subject)"}</div>
                </div>
                <div className="text-[10px] font-mono text-[var(--peak-muted)] shrink-0">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
